import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { apis } from "../apis";
import { 
  ArrowLeft, 
  RefreshCw, 
  Smile, 
  Meh, 
  Frown 
} from "lucide-react";
import "./Reviews.css";

ChartJS.register(Tooltip, Legend, ArcElement);

// Custom Components
const Card = ({ children, className = "" }) => (
  <div className={`card ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", disabled, className = "", icon }) => (
  <button 
    className={`btn btn-${variant} ${className}`} 
    onClick={onClick} 
    disabled={disabled}
  >
    {icon && <span className="btn-icon">{icon}</span>}
    {children}
  </button>
);

const Toast = ({ message, type, onClose }) => (
  <div className={`toast toast-${type}`}>
    <span>{message}</span>
    <button onClick={onClose}>&times;</button>
  </div>
);

const ReviewsTable = ({ reviews }) => {
  // Function to get smiley icons for review values
  const getSmileyIcon = (value) => {
    switch (value) {
      case "good":
        return <Smile color="#68a357" size={24} />; // Green smiley face for "good"
      case "notBad":
        return <Meh color="#da9b2b" size={24} />; // Yellow neutral face for "not bad"
      case "notGood":
        return <Frown color="#bf644f" size={24} />; // Red frown face for "not good"
      default:
        return null;
    }
  };

  return (
    <div className="reviews-table">
      <table>
        <thead>
          <tr>
            <th style={{ textAlign: 'right' }}>الرقم</th>
            <th style={{ textAlign: 'right' }}>مراجعة الفحوى والمضمون</th>
            <th style={{ textAlign: 'right' }}>مراجعة الأداء</th>
            <th style={{ textAlign: 'right' }}>مراجعة المؤثرات الصوتية</th>
            <th style={{ textAlign: 'right' }}>مراجعة الموسيقى</th>
          </tr>
        </thead>
        <tbody>
          {reviews.map((review, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{getSmileyIcon(review.content)}</td>
              <td>{getSmileyIcon(review.style)}</td>
              <td>{getSmileyIcon(review.sound)}</td>
              <td>{getSmileyIcon(review.music)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReviewsChart = ({ title, data }) => (
  <Card className="chart-card">
    <h3>{title}</h3>
    <div className="chart-container">
      <Pie data={data} />
    </div>
  </Card>
);

const StoryReviews = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const storyId = searchParams.get("id");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (storyId) {
      fetchReviews();
    }
  }, [storyId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await apis.getReviews(storyId);
      setReviews(response.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError("فشل في جلب المراجعات.");
      setLoading(false);
    }
  };

  // Function to calculate review counts for a specific field
  const calculateReviewCounts = (field) => {
    const counts = { good: 0, notBad: 0, notGood: 0 };
    reviews.forEach((review) => {
      if (counts[review[field]] !== undefined) {
        counts[review[field]]++;
      }
    });
    return counts;
  };

  // Chart data for each field
  const createChartData = (counts) => ({
    labels: ["جيد", "لا بأس", "غير جيد"],
    datasets: [
      {
        data: [counts.good, counts.notBad, counts.notGood],
        backgroundColor: ["#68a357", "#da9b2b", "#bf644f"],
      },
    ],
  });

  // Calculate counts for each review type
  const contentCounts = calculateReviewCounts("content");
  const styleCounts = calculateReviewCounts("style");
  const soundCounts = calculateReviewCounts("sound");
  const musicCounts = calculateReviewCounts("music");

  if (loading) {
    return (
      <div className="reviews-page">
        <header className="reviews-header">
          <div className="header-content">
            <h1>مراجعات القصة</h1>
            <p>جارٍ تحميل المراجعات...</p>
          </div>
          <div className="header-actions">
            <Button 
              onClick={() => navigate(-1)}
              variant="secondary"
              icon={<ArrowLeft size={18} />}
            >
              العودة للقصص
            </Button>
          </div>
        </header>
        <div className="loading-reviews">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="review-skeleton">
              <div className="skeleton-icon"></div>
              <div className="skeleton-icon"></div>
              <div className="skeleton-icon"></div>
              <div className="skeleton-icon"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reviews-page">
        <header className="reviews-header">
          <div className="header-content">
            <h1>مراجعات القصة</h1>
            <p>حدث خطأ أثناء تحميل المراجعات</p>
          </div>
          <div className="header-actions">
            <Button 
              onClick={() => navigate(-1)}
              variant="secondary"
              icon={<ArrowLeft size={18} />}
            >
              العودة للقصص
            </Button>
            <Button 
              onClick={fetchReviews}
              variant="primary"
              icon={<RefreshCw size={18} />}
            >
              إعادة المحاولة
            </Button>
          </div>
        </header>
        <div className="error-message">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reviews-page">
      <header className="reviews-header">
        <div className="header-content">
          <h1>مراجعات القصة</h1>
          <p>{reviews.length} مراجعة</p>
        </div>
        <div className="header-actions">
          <Button 
            onClick={() => navigate(-1)}
            variant="secondary"
            icon={<ArrowLeft size={18} />}
          >
            العودة للقصص
          </Button>
          <Button 
            onClick={fetchReviews}
            variant="refresh"
            icon={<RefreshCw size={18} />}
          >
            تحديث
          </Button>
        </div>
      </header>

      <div className="reviews-content">
        <Card className="reviews-table-card">
          <h2>جدول المراجعات</h2>
          <ReviewsTable reviews={reviews} />
        </Card>

        <div className="reviews-charts">
          <ReviewsChart 
            title="مراجعات الفحوى والمضمون" 
            data={createChartData(contentCounts)} 
          />
          <ReviewsChart 
            title="مراجعات الأداء" 
            data={createChartData(styleCounts)} 
          />
          <ReviewsChart 
            title="مراجعات المؤثرات الصوتية" 
            data={createChartData(soundCounts)} 
          />
          <ReviewsChart 
            title="مراجعات الموسيقى" 
            data={createChartData(musicCounts)} 
          />
        </div>
      </div>
    </div>
  );
};

export default StoryReviews;
